import { Injectable } from '@angular/core';

export interface Condition {
  field?: string; // Field name to compare, optional for nested conditions
  operator?: 'equals' | 'not-equal' | 'less-than' | 'greater-than' | 'less-than-or-equals' | 'greater-than-or-equals' | 'contains'; // Operators
  value?: string | number | Date | string[]; // Comparison value
  nestedConditions?: Condition[]; // Nested conditions for AND/OR
  combine?: 'AND' | 'OR'; // Combination type for nested conditions
}
/* 
Example 1: Single Condition
const condition: Condition = {
  field: 'age',
  operator: 'greater-than-or-equals',
  value: 18,
};

const actualValues = { age: 20 };
const result = conditionEvaluatorService.evaluate(actualValues, condition);
console.log(result); // true
 */

/*
Example 2: Nested Conditions with AND 
const condition: Condition = {
  nestedConditions: [
    { field: 'age', operator: 'greater-than-or-equals', value: 18 },
    { field: 'age', operator: 'less-than', value: 30 },
  ],
  combine: 'AND',
};

const actualValues = { age: 25 };
const result = conditionEvaluatorService.evaluate(actualValues, condition);
console.log(result); // true
 */
/* 
Example 3: Nested Conditions with OR
const condition: Condition = {
  nestedConditions: [
    { field: 'age', operator: 'equals', value: 18 },
    { field: 'age', operator: 'greater-than', value: 30 },
  ],
  combine: 'OR',
};

const actualValues = { age: 35 };
const result = conditionEvaluatorService.evaluate(actualValues, condition);
console.log(result); // true
 */

/* 
Example 4: Complex Nested Conditions
const condition: Condition = {
  nestedConditions: [
    {
      field: 'age',
      operator: 'greater-than',
      value: 18,
    },
    {
      nestedConditions: [
        { field: 'city', operator: 'equals', value: 'New York' },
        { field: 'status', operator: 'equals', value: 'Active' },
      ],
      combine: 'AND',
    },
  ],
  combine: 'AND',
};

const actualValues = { age: 25, city: 'New York', status: 'Active' };
const result = conditionEvaluatorService.evaluate(actualValues, condition);
console.log(result); // true
 */

@Injectable({
  providedIn: 'root',
})
export class ConditionEvaluatorService {
  // Evaluate a single condition
  private evaluateCondition(
    actualValue: string | number | Date | string[],
    condition: Condition
  ): boolean {
    if (!condition.field || !condition.operator) {
      throw new Error('Invalid condition: Missing field or operator');
    }

    switch (condition.operator) {
      case 'equals':
        return actualValue === condition.value;
      case 'not-equal':
        return actualValue !== condition.value;
      case 'less-than':
        return actualValue < condition.value;
      case 'greater-than':
        return actualValue > condition.value;
      case 'less-than-or-equals':
        return actualValue <= condition.value;
      case 'greater-than-or-equals':
        return actualValue >= condition.value;
      case 'contains':
        if (Array.isArray(actualValue) && typeof condition.value === 'string') {
          return actualValue.includes(condition.value);
        }
        throw new Error('Invalid use of "contains" operator');
      default:
        throw new Error(`Unsupported operator: ${condition.operator}`);
    }
  }

  // Evaluate nested conditions or a single condition
  public evaluate(
    actualValues: { [key: string]: string | number | Date | string[] },
    condition: Condition
  ): boolean {
    if (condition.nestedConditions) {
      return this.evaluateConditionList(
        actualValues,
        condition.nestedConditions,
        condition.combine || 'AND'
      );
    }

    const actualValue = actualValues[condition.field!];
    if (actualValue === undefined) {
      throw new Error(`Field "${condition.field}" not found in actual values`);
    }

    return this.evaluateCondition(actualValue, condition);
  }

  // Evaluate a list of conditions
  private evaluateConditionList(
    actualValues: { [key: string]: string | number | Date | string[] },
    conditions: Condition[],
    combine: 'AND' | 'OR'
  ): boolean {
    if (!conditions.length) {
      return false;
    }

    if (combine === 'AND') {
      return conditions.every((condition) => this.evaluate(actualValues, condition));
    }

    if (combine === 'OR') {
      return conditions.some((condition) => this.evaluate(actualValues, condition));
    }

    throw new Error(`Unsupported combination type: ${combine}`);
  }
}
